import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Bookmark, ArrowLeft, Globe, Lock, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { collectionsAPI } from '../services/api';
import Navbar from '../components/Navbar';
import PromptCard from '../components/PromptCard';

/**
 * CollectionDetail Page - View prompts in a collection
 */
const CollectionDetail = () => {
  const { id } = useParams();
  const { userProfile } = useAuth();

  const [collection, setCollection] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadCollection = useCallback(async () => {
    try {
      setLoading(true);
      const response = await collectionsAPI.getById(id);
      setCollection(response.data.collection);
      setPrompts(response.data.prompts || []);
      setError(null);
    } catch (err) {
      console.error('Error loading collection:', err);
      setError(err.response?.data?.error || 'Failed to load collection');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadCollection();
  }, [loadCollection]);

  const handleRemovePrompt = async (promptId) => {
    if (!window.confirm('Remove this prompt from the collection?')) {
      return;
    }

    try {
      await collectionsAPI.removePrompt(id, promptId);
      setPrompts(prompts.filter(p => p.id !== promptId));

      // Update collection prompt count
      if (collection) {
        setCollection({
          ...collection,
          prompt_count: (collection.prompt_count || 1) - 1
        });
      }
    } catch (err) {
      console.error('Error removing prompt:', err);
      alert(err.response?.data?.error || 'Failed to remove prompt');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="text-gray-500 dark:text-gray-400">Loading collection...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="text-red-600 dark:text-red-400 mb-4">{error}</div>
          <Link
            to="/collections"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
          >
            <ArrowLeft size={20} />
            Back to Collections
          </Link>
        </div>
      </div>
    );
  }

  if (!collection) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Navbar />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="text-gray-500 dark:text-gray-400 mb-4">Collection not found</div>
          <Link
            to="/collections"
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
          >
            <ArrowLeft size={20} />
            Back to Collections
          </Link>
        </div>
      </div>
    );
  }

  const isOwner = userProfile && collection.user_id === userProfile.id;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navbar />

      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <Link
          to="/collections"
          className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline mb-6"
        >
          <ArrowLeft size={20} />
          Back to Collections
        </Link>

        {/* Collection Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-start gap-4">
              <Bookmark size={32} className="text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {collection.name}
                  </h1>
                  {collection.is_public ? (
                    <Globe size={20} className="text-gray-400" title="Public collection" />
                  ) : (
                    <Lock size={20} className="text-gray-400" title="Private collection" />
                  )}
                </div>
                {collection.description && (
                  <p className="text-gray-600 dark:text-gray-400 mb-3">
                    {collection.description}
                  </p>
                )}
                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                  <span>{collection.prompt_count || 0} prompts</span>
                  <span>|</span>
                  <span>Created {new Date(collection.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Prompts Grid */}
        {prompts.length === 0 ? (
          <div className="text-center py-16">
            <Bookmark size={64} className="mx-auto text-gray-400 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              No prompts yet
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              {isOwner
                ? 'Start adding prompts to this collection by clicking the bookmark button on any prompt'
                : 'This collection is empty'
              }
            </p>
            {isOwner && (
              <Link
                to="/dashboard"
                className="inline-block px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
              >
                Browse Prompts
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {prompts.map((prompt) => (
              <div key={prompt.id} className="relative group">
                <PromptCard prompt={prompt} />

                {/* Remove Button (only for collection owner) */}
                {isOwner && (
                  <button
                    onClick={() => handleRemovePrompt(prompt.id)}
                    className="absolute top-2 right-2 p-2 bg-white dark:bg-gray-800 rounded-lg shadow-md opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100 dark:hover:bg-red-900 text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                    title="Remove from collection"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionDetail;


